import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { api, type HotelRoomPhotos } from '../api';
import { OdaDagilimi } from '../OdaDagilimi';
import { TarihAraligiSecici } from '../TarihSecici';
import { Alan, Notices } from '../Workflow';
import { ROOM_PAGE_SIZE, geceFarki, roomPicture } from '../hotel-offer-utils';
import { OTEL_TEKLIF_METIN as M } from '../metin/otelTeklif';
import { searchKey } from '../sales-catalogue';
import { otelTeklifApi } from './istemci';
import type { Dil, MusaitlikCevabi, MusaitlikIstegi, OdaTeklifi, OtelBilgisi, OtelOzeti } from './tipler';
import '../turBilgi/turBilgi.css';
import './otelTeklif.css';

type OdaTaslagi = { id: string; yetiskin: string; cocukYaslari: string[] };
const yeniOda = (): OdaTaslagi => ({ id: crypto.randomUUID(), yetiskin: '2', cocukYaslari: [] });
const hataMetni = (e: unknown) => e instanceof Error ? e.message : M.hata;
const tutar = (minor: number, birim: string) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: birim }).format(minor / 100);
const saatMetni = (iso: string) => new Intl.DateTimeFormat('tr-TR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Dubai' }).format(new Date(iso));
const gecelik = (t: OdaTeklifi, gece: number) => gece > 0 ? Math.round(t.fiyat.toplamMinor / gece) : t.fiyat.toplamMinor;
const iptalMetni = (t: OdaTeklifi) => t.iptal.iadeEdilebilir === true && t.iptal.sonTarih ? M.iptalUcretsiz(saatMetni(t.iptal.sonTarih)) : t.iptal.iadeEdilebilir === false ? M.iptalYok : M.iptalBilinmiyor;
const odaAdi = (t: OdaTeklifi) => t.odaTipleri.join(' + ');

/** Süzme: her sözcük oda adında ya da pansiyonda geçmeli, Türkçe harften bağımsız. */
function teklifleriSuz(teklifler: readonly OdaTeklifi[], sorgu: string): OdaTeklifi[] {
  const sozcukler = searchKey(sorgu).trim().split(/\s+/).filter(Boolean);
  if (!sozcukler.length) return [...teklifler];
  return teklifler.filter(t => { const metin = searchKey(`${odaAdi(t)} ${t.pansiyon}`); return sozcukler.every(s => metin.includes(s)); });
}

/** Tur Bilgi Formu'nun otel karşılığı: numarayla bul, tarih ve kişi seç, odalardan seç, PDF teklif indir. */
export function OtelTeklif() {
  const id = useId();
  const [arama, setArama] = useState('');
  const [sorgu, setSorgu] = useState({ q: '', sira: 0 });
  const [sonuclar, setSonuclar] = useState<readonly OtelOzeti[]>([]);
  const [aramaYuk, setAramaYuk] = useState(false);
  const [aramaHata, setAramaHata] = useState<string | null>(null);
  const [slug, setSlug] = useState('');
  const [otel, setOtel] = useState<OtelBilgisi | null>(null);
  const [otelYuk, setOtelYuk] = useState(false);
  const [otelHata, setOtelHata] = useState<string | null>(null);
  const [otelTekrar, setOtelTekrar] = useState(0);
  const [dil, setDil] = useState<Dil>('tr');
  const [giris, setGiris] = useState('');
  const [cikis, setCikis] = useState('');
  const [uyruk, setUyruk] = useState('TR');
  const [odalar, setOdalar] = useState<OdaTaslagi[]>(() => [yeniOda()]);
  const [sonuc, setSonuc] = useState<{ key: string; veri: MusaitlikCevabi } | null>(null);
  const [sorguYuk, setSorguYuk] = useState(false);
  const [sorguHata, setSorguHata] = useState<string | null>(null);
  const [secili, setSecili] = useState<readonly string[]>([]);
  const [sayfa, setSayfa] = useState(1);
  const [odaSorgu, setOdaSorgu] = useState('');
  const [kareler, setKareler] = useState<HotelRoomPhotos | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [islemHata, setIslemHata] = useState<string | null>(null);
  const [bildirim, setBildirim] = useState<string | null>(null);
  const [, tik] = useState(0);
  const sorguAbort = useRef<AbortController | null>(null);
  const ekranRef = useRef<HTMLDivElement>(null);

  const imza = JSON.stringify({ slug, giris, cikis, uyruk, odalar: odalar.map(o => [o.yetiskin, o.cocukYaslari]) });
  const onceki = useRef(imza);
  // Konaklama bilgisi değişince önceki oda fiyatları ve seçim geçersizdir: yeniden sorgulanmalı.
  useEffect(() => {
    if (onceki.current === imza) return;
    onceki.current = imza;
    sorguAbort.current?.abort(); sorguAbort.current = null;
    setSonuc(null); setSecili([]); setSorguHata(null); setSorguYuk(false); setBildirim(null); setIslemHata(null);
  }, [imza]);

  useEffect(() => {
    const c = new AbortController(); setSonuclar([]); setAramaHata(null);
    if (!sorgu.q) { setAramaYuk(false); return () => c.abort(); }
    setAramaYuk(true);
    otelTeklifApi.ara(sorgu.q, c.signal).then(v => { if (!c.signal.aborted) setSonuclar(v.sonuclar); }).catch(e => { if (!c.signal.aborted) setAramaHata(hataMetni(e)); }).finally(() => { if (!c.signal.aborted) setAramaYuk(false); });
    return () => c.abort();
  }, [sorgu]);

  useEffect(() => {
    const c = new AbortController(); setOtel(null); setOtelHata(null); setKareler(null);
    if (!slug) return () => c.abort();
    setOtelYuk(true);
    otelTeklifApi.otel(slug, dil, c.signal).then(v => { if (!c.signal.aborted) setOtel(v); }).catch(e => { if (!c.signal.aborted) setOtelHata(hataMetni(e)); }).finally(() => { if (!c.signal.aborted) setOtelYuk(false); });
    // Oda kareleri ayrı okunur; okunamazsa kartlar karesiz çizilir.
    api.hotelRoomPhotos(slug, c.signal).then(v => { if (!c.signal.aborted) setKareler(v); }).catch(() => undefined);
    return () => c.abort();
  }, [slug, dil, otelTekrar]);

  const veri = sonuc?.key === imza ? sonuc.veri : null;
  useEffect(() => {
    if (!veri?.musait) return undefined;
    const t = window.setInterval(() => tik(n => n + 1), 15_000);
    return () => window.clearInterval(t);
  }, [veri]);
  const bitis = veri?.gecerlilikBitisi ? Date.parse(veri.gecerlilikBitisi) : NaN;
  const suresiDoldu = !!veri?.musait && Number.isFinite(bitis) && bitis <= Date.now();

  const suzulen = useMemo(() => veri ? teklifleriSuz(veri.teklifler, odaSorgu) : [], [veri, odaSorgu]);
  const sayfaSayisi = Math.max(1, Math.ceil(suzulen.length / ROOM_PAGE_SIZE));
  const buSayfa = Math.min(sayfa, sayfaSayisi);
  const secilenTeklifler = useMemo(() => veri ? veri.teklifler.filter(t => secili.includes(t.teklifNo)) : [], [veri, secili]);

  function odaDegistir(odaId: string, yama: Partial<OdaTaslagi>) { setOdalar(v => v.map(o => o.id === odaId ? { ...o, ...yama } : o)); }

  async function odalariGetir() {
    if (!slug || sorguYuk) return;
    setSorguHata(null);
    let istek: MusaitlikIstegi;
    try {
      if (!giris || !cikis || (geceFarki(giris, cikis) ?? 0) < 1) throw new Error(M.tarihGecersiz);
      if (!/^[A-Z]{2}$/.test(uyruk.trim())) throw new Error(M.uyrukGecersiz);
      const duzen = odalar.map(o => {
        if (!/^\d+$/.test(o.yetiskin) || Number(o.yetiskin) < 1 || Number(o.yetiskin) > 10) throw new Error(M.yetiskinGecersiz);
        if (o.cocukYaslari.some(y => !/^\d+$/.test(y) || Number(y) > 17)) throw new Error(M.yasGecersiz);
        return { yetiskin: Number(o.yetiskin), cocukYaslari: o.cocukYaslari.map(Number) };
      });
      istek = { girisTarihi: giris, cikisTarihi: cikis, odalar: duzen, uyruk: uyruk.trim() };
    } catch (e) { setSorguHata(hataMetni(e)); return; }
    const c = new AbortController(); sorguAbort.current = c;
    const anahtar = imza;
    setSorguYuk(true); setSonuc(null); setSecili([]); setSayfa(1); setOdaSorgu(''); setBildirim(null); setIslemHata(null);
    const zamanAsimi = window.setTimeout(() => c.abort(), 40_000);
    try {
      const cevap = await otelTeklifApi.musaitlik(slug, istek, c.signal);
      if (c.signal.aborted) return;
      setSonuc({ key: anahtar, veri: cevap });
    } catch (e) {
      setSorguHata(c.signal.aborted ? M.zamanAsimi : hataMetni(e));
    } finally {
      window.clearTimeout(zamanAsimi);
      if (sorguAbort.current === c) { sorguAbort.current = null; setSorguYuk(false); }
    }
  }

  function secimiDegistir(teklifNo: string) {
    setSecili(v => v.includes(teklifNo) ? v.filter(x => x !== teklifNo) : [...v, teklifNo]);
    setBildirim(null);
  }

  const engel = !otel ? M.baslangic : !otel.fiyatlanabilir ? M.fiyatlanamaz : !veri?.musait || !veri.aramaNo ? M.sorgula : suresiDoldu ? M.sureDoldu : secili.length === 0 ? M.secilenYok : null;

  async function pdfIndir() {
    if (engel || mesgul || !otel || !veri?.aramaNo) return;
    const c = new AbortController(); setMesgul(true); setIslemHata(null); setBildirim(null);
    try {
      const { blob, dosyaAdi } = await otelTeklifApi.pdf({ slug: otel.slug, aramaNo: veri.aramaNo, teklifNolari: secili.filter(no => veri.teklifler.some(t => t.teklifNo === no)), dil }, c.signal);
      const adres = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = adres; a.download = dosyaAdi; document.body.append(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(adres), 10_000);
      setBildirim(M.pdfHazir);
    } catch (e) { setIslemHata(hataMetni(e)); } finally { setMesgul(false); }
  }

  useEffect(() => {
    const frame = requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'instant' }));
    return () => cancelAnimationFrame(frame);
  }, []);

  return <div ref={ekranRef} className="tb-ekran ot-ekran" data-testid="otel-teklif">
    <div className="tb-giris"><p className="muted">{M.tarif}</p><p className="muted tb-kaynak">{M.musteriVerisi}</p></div>
    <div className="tb-masa">
      <aside className="card tb-arama" id="ot-arama" aria-label={M.ara}>
        <form onSubmit={e => { e.preventDefault(); setSorgu(s => ({ q: arama.trim(), sira: s.sira + 1 })); }}>
          <Alan etiket={M.ara} ipucu={M.aramaIpucu}><input type="search" value={arama} onChange={e => setArama(e.target.value)} disabled={mesgul} /></Alan>
          <button type="submit" disabled={arama.trim().length < 2 || mesgul}>{M.araDugme}</button>
        </form>
        {aramaYuk && <p role="status">{M.yukleniyor}</p>}{aramaHata && <p className="error" role="alert">{aramaHata}</p>}
        {!aramaYuk && !aramaHata && sorgu.q && !sonuclar.length && <p className="empty-state" role="status">{M.aramaBos}</p>}
        <ul className="tb-sonuclar" aria-label={M.sonuclar}>{sonuclar.map(o => <li key={o.slug}><button type="button" disabled={mesgul || !o.yayinda || !o.fiyatlanabilir} aria-current={slug === o.slug ? 'true' : undefined} onClick={() => setSlug(o.slug)}>
          <strong>{o.ad}</strong><span>{M.otelNo}: {o.otelNo ?? M.numarasiz}</span>
          <small>{[o.sehir, o.ulke, o.yildiz ? M.yildiz(o.yildiz) : null].filter(Boolean).join(' · ')}</small>
          {!o.yayinda ? <span className="state">{M.yayindaDegil}</span> : !o.fiyatlanabilir ? <span className="state">{M.fiyatsizSatir}</span> : null}
        </button></li>)}</ul>
      </aside>

      <section className="tb-bilgi" aria-live="polite">
        {otelYuk ? <div className="card" role="status">{M.yukleniyor}</div>
          : otelHata ? <div className="card error" role="alert">{otelHata}<button type="button" onClick={() => setOtelTekrar(n => n + 1)}>{M.yeniden}</button></div>
          : !otel ? <div className="card empty-state">{M.baslangic}</div>
          : <>
            <div className="card ot-otel">
              <div className="tb-baslik-satir"><div>
                <h2 className="ot-otel-ad">{otel.ad}</h2>
                <p className="tb-meta">{[otel.sehir, otel.ulke, otel.yildiz ? M.yildiz(otel.yildiz) : null].filter(Boolean).join(' · ')}</p>
              </div><p className="tb-tur-no"><span>{M.otelNo}</span><strong data-testid="ot-otel-no">{otel.otelNo}</strong></p></div>
              <p><a href={otel.sayfaAdresi} target="_blank" rel="noopener noreferrer">{M.otelSayfasiAc}</a></p>
              {!otel.fiyatlanabilir && <p className="tb-uyari" role="status">{M.fiyatlanamaz}</p>}
            </div>

            <fieldset className="card ot-konaklama" disabled={!otel.fiyatlanabilir}>
              <legend>{M.konaklama}</legend>
              <div className="ot-alanlar">
                <div className="ot-alan"><label htmlFor={`${id}-tarih`}>{M.tarihler}</label><TarihAraligiSecici id={`${id}-tarih`} testId="ot-tarih" giris={giris} cikis={cikis} setAralik={(g, c) => { setGiris(g); setCikis(c); }} /></div>
                <label className="ot-alan">{M.uyruk}<input data-testid="ot-uyruk" maxLength={2} autoComplete="off" placeholder={M.uyrukOrnek} value={uyruk} onChange={e => setUyruk(e.target.value.toUpperCase())} /></label>
              </div>
              <OdaDagilimi
                odalar={odalar} onek="ot" metin={M} devreDisi={false}
                siniflar={{ kap: 'ot-odalar', oda: 'ot-oda', alanlar: 'ot-oda-alanlar', yaslar: 'ot-yaslar' }}
                degistir={(odaId, yama) => odaDegistir(odaId, yama)}
                kaldir={(odaId) => setOdalar(v => v.filter(o => o.id !== odaId))}
              />
              <div className="ot-eylem"><button type="button" disabled={odalar.length >= 8} onClick={() => setOdalar(v => [...v, yeniOda()])}>{M.odaEkle}</button><button type="button" className="primary" data-testid="ot-sorgula" disabled={sorguYuk} onClick={() => void odalariGetir()}>{sorguYuk ? M.sorgulaniyor : M.sorgula}</button></div>
              {sorguHata && <p className="error" role="alert">{sorguHata}</p>}
            </fieldset>

            {veri && !veri.musait && <p className="card tb-uyari" role="status">{veri.neden === 'LIVE_PRICE_UNKNOWN' ? M.fiyatBilinmiyor : /^MIN_NIGHTS_\d+$/.test(veri.neden ?? '') ? M.minGece((veri.neden ?? '').slice(11)) : M.odaYok}</p>}
            {veri?.musait && <div className="card ot-sonuc" data-testid="ot-sonuc">
              <div className="ot-sonuc-baslik"><strong>{M.secenekSayisi(veri.teklifler.length)}</strong><span>{M.gece(veri.konaklama.geceSayisi)}{veri.gecerlilikBitisi ? ` · ${M.fiyatSuresi(saatMetni(veri.gecerlilikBitisi))}` : ''}</span></div>
              {veri.onbellektenMi && <p className="muted">{M.onbellek}</p>}
              {suresiDoldu && <p className="tb-uyari" role="status">{M.sureDoldu}</p>}
              {veri.teklifler.length === 0 ? <p role="status">{M.odaYok}</p> : <label className="ot-oda-ara">{M.odaAra}<input type="search" autoComplete="off" maxLength={80} placeholder={M.odaAraIpucu} value={odaSorgu} onChange={e => { setOdaSorgu(e.target.value); setSayfa(1); }} /></label>}
              {veri.teklifler.length > 0 && suzulen.length === 0 && <p role="status">{M.odaAraBos}</p>}
              <div className="ot-kartlar">{suzulen.slice((buSayfa - 1) * ROOM_PAGE_SIZE, buSayfa * ROOM_PAGE_SIZE).map((t, i) => {
                const resim = roomPicture(kareler, t.odaTipleri);
                const dahil = secili.includes(t.teklifNo);
                return <article key={t.teklifNo} className={`ot-kart${dahil ? ' ot-kart-secili' : ''}`} data-testid={`ot-teklif-${i}`}>
                  {resim ? <img className="ot-foto" src={resim.src} srcSet={resim.srcset || undefined} sizes="160px" alt={resim.alt || odaAdi(t)} loading="lazy" decoding="async" /> : <span className="ot-foto ot-foto-bos" aria-hidden="true" />}
                  <div className="ot-kart-govde">
                    <h4>{odaAdi(t)}</h4><p>{t.pansiyon || M.pansiyonYok}</p>
                    <strong>{tutar(t.fiyat.toplamMinor, t.fiyat.paraBirimi)}</strong><small>{M.toplamKonaklama}</small>
                    <small data-testid={`ot-iptal-${i}`}>{iptalMetni(t)}</small>
                    <small>{M.gecelik}: {tutar(gecelik(t, veri.konaklama.geceSayisi), t.fiyat.paraBirimi)}</small>
                    <button type="button" aria-pressed={dahil} disabled={suresiDoldu} data-testid={`ot-sec-${i}`} onClick={() => secimiDegistir(t.teklifNo)}>{dahil ? M.secimiKaldir : M.sec}</button>
                  </div>
                </article>;
              })}</div>
              {sayfaSayisi > 1 && <nav className="ot-sayfa" aria-label={M.sayfalar}><button type="button" disabled={buSayfa <= 1} onClick={() => setSayfa(buSayfa - 1)}>{M.oncekiSayfa}</button><span>{M.sayfaBilgisi(buSayfa, sayfaSayisi, suzulen.length)}</span><button type="button" disabled={buSayfa >= sayfaSayisi} onClick={() => setSayfa(buSayfa + 1)}>{M.sonrakiSayfa}</button></nav>}
              <p className="muted">{M.vergiNotu}</p>
            </div>}
          </>}
      </section>

      {otel && <aside className="tb-ray" aria-label={M.pdfRay}>
        <fieldset disabled={mesgul} className="card tb-ayar"><legend>{M.pdfAyar}</legend>
          <div><span className="eyebrow">{M.dil}</span><div className="tb-secim" role="group" aria-label={M.dil}>{(['tr', 'en'] as const).map(d => <button key={d} type="button" aria-pressed={dil === d} onClick={() => setDil(d)}>{M.diller[d]}</button>)}</div></div>
        </fieldset>
        <div className="card tb-fiyat" aria-live="polite" data-testid="ot-secilenler"><span className="eyebrow">{M.secilenOdalar}</span>
          {secilenTeklifler.length === 0 ? <p>{M.secilenYok}</p> : <ul>{secilenTeklifler.map(t => <li key={t.teklifNo}>{odaAdi(t)}, {t.pansiyon}: <strong>{tutar(t.fiyat.toplamMinor, t.fiyat.paraBirimi)}</strong></li>)}</ul>}
        </div>
        <div className="card tb-gonderim">
          {engel && <p className="tb-uyari tb-engel" id="ot-engel" role="status"><strong>{M.pdfYapilamaz}</strong> {engel}</p>}
          <button className="primary full" type="button" data-testid="ot-pdf" disabled={!!engel || mesgul} aria-describedby={engel ? 'ot-engel' : undefined} onClick={() => void pdfIndir()}>{mesgul ? M.pdfHazirlaniyor : M.pdf}</button>
          <Notices scope="otel-teklif-islem" error={islemHata} notice={bildirim} />
        </div>
      </aside>}
    </div>
  </div>;
}
